<script setup>
import { computed, ref } from "vue";
import { useEdaConnectionStore } from "@/stores/edaConnectionStore";
import { useI18n } from 'vue-i18n';
import { useFrontEndStore } from "@/stores/frontEndStore";
import router from "@/router/routerSetup";
import { useCommunitiesStore } from "@/stores/communitiesStore";
import { DialogConfirm, useSnackbarStore, PrimaryButton } from '@es-bill/es-bill-common-components';

const { t } = useI18n();
const edaConnectionStore = useEdaConnectionStore();
const frontEndStore = useFrontEndStore();
const communitiesStore = useCommunitiesStore();
const snackbarStore = useSnackbarStore();

const sortBy = [{ key: 'ecn', order: 'asc' }];

const tableHeader = computed(() => [
  { title: 'ECN', value: 'ecn' },
  { title: t('name'), value: 'name' },
  { title: t('status'), value: 'status' },
  { title: t('actions'), value: 'actions', sortable: false },
]);

edaConnectionStore.fetchEdaConnections();

const communitiesAvailable = computed(() => communitiesStore.allCommunities.map(c => ({...c, props: {subtitle: c.ecn + ' / ' + c.ecid}})));


const edaConnections = computed(() => {
  let connections = edaConnectionStore.connections;

  // Apply EDA filter
  if (frontEndStore.edaFilter.ecnList.length)
    connections = connections.filter(connection => frontEndStore.edaFilter.ecnList.includes(connection.ecn));

  // add name to each connection
  connections = connections.map(connection => {
    const community = communitiesStore.allCommunities.find(c => c.ecn === connection.ecn);
    connection.name = community ? community.name : 'Unknown Community';
    return connection;
  });

  return connections;
});
const isDialogVisible = ref(false);
const connectionId = ref(null); // To store the selected EDA Connection ID for deletion

function showDeleteDialog(id) {
  console.log(id)
  connectionId.value = id;
  isDialogVisible.value = true;
}

async function handleConfirm() {
  if (connectionId.value) {
    try {
      await edaConnectionStore.deleteEdaConnection(connectionId.value);
      snackbarStore.showSnackbar(t('applicantMessages.deletedSuccess'), '', 'Success', 4000, []);
    } catch (error) {
      snackbarStore.showSnackbar(error?.response?.data?.message || t('unexpectedError'), '', 'Error', 10000, []);
    }
    connectionId.value = null;
  }
  isDialogVisible.value = false;
}

function handleCancel() {
  connectionId.value = null; // Reset the selected ECN
  isDialogVisible.value = false;
}
</script>

<template>
  <v-container class="pa-0">
    <div class="d-flex align-center mb-4">
      <p class="textHeadline flex-grow-1">{{ $t('edaConnections') }}</p>
    </div>

    <div class="d-flex flex-column flex-md-row w-100 mb-3">
        <v-autocomplete
          class="w-100"
          bg-color="white"
          :label="$t('community')"
          v-model="frontEndStore.edaFilter.ecnList"
          :items="communitiesAvailable"
          :filter-keys="['title','props.subtitle']"
          multiple
          variant="outlined"
          item-value="ecn"
          item-title="ecn"
          clearable
          hide-details
        ></v-autocomplete>
        <PrimaryButton prepend-icon="mdi-plus" :text="t('createEdaConnection')" @click="router.push({ name: 'edaConnectionForm' })"/>
      </div>

      <v-data-table
        class="elevation-2 mainViewContent"
        :sort-by="sortBy"
        :headers="tableHeader"
        :items="edaConnections">

        <!-- Custom slots for status column -->
        <template v-slot:[`item.status`]="{ item }">
          <span>{{ t(`${item.status.toLowerCase()}`) }}</span>
        </template>

        <!-- Actions column -->
        <template v-slot:[`item.actions`]="{ item }">
          <v-icon
            @click="showDeleteDialog(item._id)"
            class="mr-2"
          >mdi-delete
          </v-icon>
          <v-icon
            @click="
             router.push({
               name: 'edaConnectionForm',
               params: { id: item._id }
             })"
          >mdi-pencil
          </v-icon>
        </template>
      </v-data-table>

    <DialogConfirm
      :modelValue="isDialogVisible"
      @update:modelValue="isDialogVisible = $event"
      @confirm="handleCancel"
      @cancel="handleConfirm"
      :title="t('areYouSure')"
      :message="t('deleteConfirmationGeneric')"
      :confirmText="t('cancel')"
      :cancelText="t('confirm')"
    />
  </v-container>
</template>

<style scoped>
</style>

